import React, { useContext, useMemo, useState } from "react";
import { SettingsContext } from "./SettingsProvider";
import PropTypes from "prop-types";
import MetricTypes from "../utils/metric-types";
import KPIImpactFormats from "../utils/kpi-impact-formats";
import { applyStringTemplate, formatRangeNumber } from "../utils/formatting";
import { useStringTemplate } from "./StringProvider";
import styled from "styled-components";
import Tooltip from "./Tooltip";
import { Title } from "../components/Typography";
import classNames from "classnames";

const RangeWrapper = ({ type, fullText, tooltip, children }) => {
  const [truncated, setTruncated] = useState(false);

  const onEllipsis = () => {
    setTruncated(true);
  };

  const currentTooltip = useMemo(() => {
    if (!truncated || !fullText) {
      return tooltip;
    }

    if (!tooltip) {
      return fullText;
    }

    return (
      <>
        {fullText}
        <br />
        <br />
        {tooltip}
      </>
    );
  }, [tooltip, fullText, truncated]);

  if (type === KPIImpactFormats.CUSTOM) {
    return (
      <Tooltip2
        className={classNames({ hasTooltip: !!currentTooltip })}
        title={currentTooltip}
      >
        <Title
          level={4}
          style={{ color: "inherit" }}
          ellipsis={{
            rows: 3,
            expandable: true,
            symbol: <></>,
            onEllipsis
          }}
        >
          {children}
        </Title>
      </Tooltip2>
    );
  }

  return tooltip || truncated ? (
    <Tooltip2
      title={currentTooltip}
      className={classNames({ hasTooltip: !!currentTooltip })}
    >
      {children}
    </Tooltip2>
  ) : (
    children
  );
};

const Tooltip2 = styled(Tooltip)`
  &.hasTooltip {
    cursor: help;
  }
`;

const Range = ({
  first,
  second,
  type = "Percentage",
  currency,
  customImpactFormat,
  impactPercentageFormulaDescription
}) => {
  const { settings } = useContext(SettingsContext);
  const { locale } = settings;
  const rangeTemplate = useStringTemplate(
    "discovery.kpi.impact.range",
    "{low} to {high}"
  );

  // Sort impact percentage ranges for display (safety net for existing data)
  let sortedFirst = first;
  let sortedSecond = second;
  if (KPIImpactFormats.ALL.includes(type)) {
    let shouldSwap;
    if (first < 0 && second < 0) {
      // Both negative: sort by absolute magnitude (smallest abs first)
      shouldSwap = Math.abs(first) > Math.abs(second);
    } else {
      // Both positive or mixed: ascending numeric order
      shouldSwap = first > second;
    }
    if (shouldSwap) {
      sortedFirst = second;
      sortedSecond = first;
    }
  }

  if (sortedFirst === sortedSecond) {
    return (
      <>
        {formatRangeNumber({
          value: sortedFirst,
          type,
          options: { locale, currency, showUnits: true, customImpactFormat }
        })}
      </>
    );
  }

  const low = formatRangeNumber({
    value: sortedFirst,
    type,
    options: { locale, currency, showUnits: false, customImpactFormat }
  });

  const high = formatRangeNumber({
    value: sortedSecond,
    type,
    options: { locale, currency, showUnits: true, customImpactFormat }
  });

  const range = applyStringTemplate({
    template: rangeTemplate,
    placeholders: {
      low,
      high
    }
  });

  let fullText = "";
  if (customImpactFormat) {
    fullText = range;
  }

  return (
    <RangeWrapper
      type={type}
      fullText={fullText}
      tooltip={impactPercentageFormulaDescription}
    >
      {range}
    </RangeWrapper>
  );
};

Range.propTypes = {
  first: PropTypes.number.isRequired,
  second: PropTypes.number.isRequired,
  type: PropTypes.oneOf([...MetricTypes.ALL, ...KPIImpactFormats.ALL])
};

export default Range;
